// contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

const initialAuthState = {
    id: null,
    token: null,
    username: null,
    name: null,
    surname: null,
    email: null,
    telephone: null,
    taxnumber: null,
    user_type: null,
    wati_account: null,
    wati_id: null,
    wati_msg_approval: null,
    wati_message_approval: null,
    admin: false,
    status: false,
    user: null,
    isAuthenticated: false
};

const createAuthenticatedState = ({ token, user = {} }) => {
    const normalizedUser = {
        id: user.id ?? null,
        username: user.username ?? null,
        name: user.name ?? null,
        surname: user.surname ?? null,
        email: user.email ?? null,
        telephone: user.telephone ?? null,
        taxnumber: user.taxnumber ?? null,
        user_type: user.user_type ?? null,
        wati_account: user.wati_account ?? null,
        wati_id: user.wati_id ?? null,
        wati_msg_approval: user.wati_msg_approval ?? null,
        wati_message_approval: user.wati_message_approval ?? user.wati_msg_approval ?? null,
        admin: user.admin ?? false,
        status: user.status ?? false
    };

    return {
        ...normalizedUser,
        token: token ?? null,
        user: normalizedUser,
        isAuthenticated: Boolean(token && normalizedUser.id)
    };
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState(initialAuthState);

    // Initialize auth state from localStorage/sessionStorage on app start
    useEffect(() => {
        const initializeAuth = () => {
            try {
                const storedAuth = localStorage.getItem('auth');
                if (storedAuth) {
                    const parsedAuth = JSON.parse(storedAuth);
                    const restoredAuth = createAuthenticatedState({
                        token: parsedAuth.token,
                        user: parsedAuth.user || parsedAuth
                    });

                    if (restoredAuth.isAuthenticated) {
                        setAuthState(restoredAuth);
                        localStorage.setItem('accessToken', restoredAuth.token);
                    } else {
                        localStorage.removeItem('auth');
                        localStorage.removeItem('accessToken');
                    }
                }
            } catch (error) {
                console.error('Error initializing auth:', error);
                // Clear corrupted storage
                localStorage.removeItem('auth');
            }
        };

        initializeAuth();
    }, []);

    // Login function
    const login = (userData) => {
        const authData = createAuthenticatedState(userData);

        if (!authData.isAuthenticated) {
            throw new Error('Invalid login payload: token and user id are required');
        }

        setAuthState(authData);
        localStorage.setItem('auth', JSON.stringify(authData));
        localStorage.setItem('accessToken', authData.token);
    };

    // Logout function
    const logout = () => {
        setAuthState(initialAuthState);
        localStorage.removeItem('auth');
        localStorage.removeItem('accessToken');
    };

    // Update user data
    const updateUser = (userData) => {
        const updatedAuth = {
            ...authState,
            user: { ...authState.user, ...userData }
        };
        setAuthState(updatedAuth);
        localStorage.setItem('auth', JSON.stringify(updatedAuth));
    };

    const value = {
        authState,
        login,
        logout,
        updateUser,
        isAuthenticated: authState.isAuthenticated
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Keep the default export for backward compatibility
export { AuthContext };
